import { faWarning } from "@fortawesome/pro-regular-svg-icons";
import { Handle, Position } from "@xyflow/react";
import { twJoin } from "tailwind-merge";

import { useVersionResourceLock } from "src/authoringMultiplayerLock/useVersionResourceLock";
import { ResourceType } from "src/clients/flow-api";
import { DEFAULT_UNTITLED_NODE_NAME } from "src/constants/DefaultValues";
import { IntegrationLatencyData } from "src/constants/NodeDataTypes";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import {
  getIntegrationLatencyTooltipText,
  IntegrationLatency,
} from "src/flowGraph/IntegrationLatency";
import { CanvasLock } from "src/multiplayer/CanvasLock";
import { useOpenOnHover } from "src/utils/useOpenOnHover";

export const handleClassName = "opacity-0";

export const renderHandles = (options?: {
  targetHandleClassName?: string;
  sourceHandleClassName?: string;
}) => (
  <>
    <Handle
      className={twJoin(handleClassName, options?.targetHandleClassName)}
      isConnectable={false}
      position={Position.Top}
      type="target"
    />
    <Handle
      className={twJoin(handleClassName, options?.sourceHandleClassName)}
      isConnectable={false}
      position={Position.Bottom}
      type="source"
    />
  </>
);
export type BaseNodeProps = {
  title: string;
  label: string;
  selected: boolean;
  id: string;
  grayedOut?: boolean;
  isErrored: boolean;
  isDeletable?: boolean;
  isInvalid?: boolean;
  icon: React.ReactNode;
  withUnderlay?: boolean;
  runIcon?: React.ReactNode;
  integrationLatencyData?: IntegrationLatencyData;
  badge?: React.ReactNode;
  commentCount?: React.ReactNode;
  renderControls?: ({
    onMouseEnter,
    onMouseLeave,
    close,
  }: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    close: () => void;
  }) => React.ReactNode;
  defaultName?: string;
};

export const BaseNode: React.FC<BaseNodeProps> = ({
  title,
  label,
  selected,
  isErrored,
  isInvalid = false,
  runIcon,
  id,
  grayedOut,
  commentCount,
  integrationLatencyData,
  badge,
  icon,
  withUnderlay = false,
  renderControls,
  defaultName = DEFAULT_UNTITLED_NODE_NAME,
}) => {
  const { isOpen, setIsOpen, onMouseEnter, onMouseLeave } = useOpenOnHover<
    HTMLDivElement,
    HTMLDivElement
  >(300);

  const { lockOwner, lockedByOtherUser } = useVersionResourceLock(
    ResourceType.NODE,
    id,
  );
  const integrationStatusTooltipText = integrationLatencyData?.integrationStatus
    ? getIntegrationLatencyTooltipText(integrationLatencyData.integrationStatus)
    : undefined;

  return (
    <>
      <div
        className={twJoin(
          "relative box-border flex w-64 flex-row rounded-lg border bg-white p-2 transition-shadow hover:cursor-pointer",
          selected &&
            !isErrored &&
            !isInvalid &&
            "border-indigo-400 outline-none ring-2 ring-indigo-500/25",
          !selected && "border-white",
          selected && isErrored && "border-red-600",
          isInvalid && selected && "border-orange-500",
          grayedOut ? "shadow-sm" : "shadow-base",
        )}
        data-loc={label}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {withUnderlay && (
          <div className="absolute inset-0 -z-10 translate-x-1.5 translate-y-1.5 rounded-xl bg-white shadow-base" />
        )}
        {icon && (
          <div
            className={twJoin(
              "h-10 w-10 shrink-0",
              grayedOut && "brightness-[1.02] grayscale",
            )}
          >
            {icon}
          </div>
        )}
        <div className="w-full overflow-hidden pl-2">
          <div className="flex items-center justify-between">
            <Tooltip
              disabled={title.length < 20}
              placement="top"
              title={title}
              asChild
            >
              <div
                className={twJoin(
                  "overflow-hidden text-ellipsis whitespace-nowrap font-inter-normal-12px",
                  grayedOut ? "text-gray-400" : "text-gray-500",
                )}
              >
                {title}
              </div>
            </Tooltip>
            {badge}
          </div>
          <div
            className={twJoin(
              "line-clamp-3 break-words font-inter-semibold-13px",
              grayedOut ? "text-gray-500" : "text-gray-800",
            )}
          >
            {label ? label : defaultName}
          </div>
        </div>
        <div
          className={twJoin(
            "ml-1 flex flex-shrink-0 flex-row",
            grayedOut && "brightness-[1.02] grayscale",
          )}
        >
          {isInvalid ? (
            <Icon icon={faWarning} size="xs" variant="warning" />
          ) : (
            commentCount
          )}
          <Tooltip
            disabled={integrationStatusTooltipText === undefined}
            placement="top"
            title={integrationStatusTooltipText}
            asChild
          >
            <div className="flex items-start">
              {runIcon}
              {integrationLatencyData && (
                <IntegrationLatency
                  integrationLatencyData={integrationLatencyData}
                />
              )}
            </div>
          </Tooltip>
        </div>
      </div>
      {lockOwner && lockedByOtherUser && (
        <CanvasLock lockOwner={lockOwner} resource="node" />
      )}
      {isOpen &&
        renderControls &&
        renderControls({
          onMouseEnter,
          onMouseLeave,
          close: () => setIsOpen(false),
        })}
      {renderHandles()}
    </>
  );
};
